import { useEffect, useRef, useState } from "react";
import {
  FOOTAGE_ACCEPT,
  readSelectedFootage,
  watchUploadInterruptions,
} from "../../../lib/upload";
import { ChoiceCard } from "../../ui/ChoiceCard";
import type { StepProps } from "../useStudioFlow";
import { StepShell } from "./StepShell";
import styles from "./SourceFootageStep.module.css";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: number): string {
  if (!seconds) return "duration unavailable";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.round(seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remaining}`;
}

export function SourceFootageStep({ draft, update, next, back }: StepProps) {
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const selectionRequestId = useRef(0);
  const isUploadMode = draft.footageChoice === "upload";
  const hasVisualFootage = draft.selectedFootage.some(
    (selected) => selected.kind === "video" || selected.kind === "image",
  );

  useEffect(() => {
    if (!isUploadMode) return undefined;
    return watchUploadInterruptions();
  }, [isUploadMode]);

  async function onFilesSelected(files: FileList | null) {
    const requestId = selectionRequestId.current + 1;
    selectionRequestId.current = requestId;
    setIsReading(true);
    setReadError(null);
    try {
      const selectedFootage = await readSelectedFootage(files);
      if (selectionRequestId.current !== requestId) return;
      update({ footageChoice: "upload", selectedFootage });
    } catch (error) {
      if (selectionRequestId.current !== requestId) return;
      setReadError(
        error instanceof Error ? error.message : "Could not read the selected footage.",
      );
    } finally {
      if (selectionRequestId.current === requestId) {
        setIsReading(false);
      }
    }
  }

  function selectPromptOnly() {
    selectionRequestId.current += 1;
    setReadError(null);
    setIsReading(false);
    update({
      footageChoice: "prompt_only",
      selectedFootage: [],
    });
  }

  return (
    <StepShell
      heading="Do you have source material for the video?"
      onNext={next}
      onBack={back}
      nextLabel="Continue →"
      nextDisabled={isReading || (isUploadMode && !hasVisualFootage)}
      stage
    >
      <div className={styles.choiceGrid}>
        <ChoiceCard
          name="footage-choice"
          checked={draft.footageChoice === "prompt_only"}
          onChange={selectPromptOnly}
          label="No"
          description="Generate the visuals from the brief."
        />

        <ChoiceCard
          name="footage-choice"
          checked={draft.footageChoice === "upload"}
          onChange={() =>
            update({
              footageChoice: "upload",
              footageMode: "hybrid",
            })
          }
          label="Yes"
          description="Upload videos, images, or audio as source material."
        />
      </div>

      {isUploadMode ? (
        <section className={styles.uploadPanel} aria-label="Selected footage">
          <div className={styles.uploadHeader}>
            <div>
              <p className={styles.uploadTitle}>Source files</p>
              <p className={styles.uploadHelp}>
                Select existing media, or record a new video or photo from this device.
              </p>
            </div>
            <div className={styles.uploadControls}>
              <label className={styles.filePicker}>
                <input
                  className={styles.hiddenFileInput}
                  type="file"
                  accept={FOOTAGE_ACCEPT}
                  multiple
                  onChange={(event) => void onFilesSelected(event.currentTarget.files)}
                />
                Select or Record Video or Photo
              </label>
              <label
                className={styles.capturePicker}
                aria-label="Record a new video or photo"
                title="Record a new video or photo"
              >
                <input
                  className={styles.hiddenFileInput}
                  type="file"
                  accept="video/*,image/*"
                  capture="environment"
                  onChange={(event) => void onFilesSelected(event.currentTarget.files)}
                />
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M8.8 6.5 10.2 5h3.6l1.4 1.5H18a3 3 0 0 1 3 3V16a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V9.5a3 3 0 0 1 3-3h2.8Z" />
                  <circle cx="12" cy="12.5" r="3.2" />
                </svg>
              </label>
            </div>
          </div>

          {isReading ? <p className={styles.status}>Reading file metadata...</p> : null}
          {readError ? <p className={styles.status}>{readError}</p> : null}

          {draft.selectedFootage.length > 0 ? (
            <ul className={styles.fileList}>
              {draft.selectedFootage.map((file) => (
                <li className={styles.fileItem} key={`${file.name}-${file.sizeBytes}`}>
                  <span className={styles.fileName}>{file.name}</span>
                  <span className={styles.fileMeta}>
                    {formatBytes(file.sizeBytes)} · {formatDuration(file.durationSec)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.status}>
              Select at least one video or image before continuing with footage.
            </p>
          )}
        </section>
      ) : null}
    </StepShell>
  );
}
